'use strict';


import { GSAPAnimationMixer, isMobile, isTablet, threeJsVariables } from '../../../settings';
import {
	CubeCamera,
	Group,
	LoadingManager,
	MathUtils,
	Matrix4,
	Scene,
	TextureLoader,
	Vector3,
	WebGLCubeRenderTarget,
} from 'three';
import { BaseElement } from '../../interfaces/BaseElement';
import { OrbitControls } from '../../../libs/OrbitControls';
import { renderUpdateValuesOnDemand } from '../../../app';
import { isBetaResourcesFolder, isPortraitMode } from '../../../functions';
import { ResizeObserver } from 'resize-observer';
import { generateRegion } from '../../threejs/sceneBackground';
import { Cloudflare } from '../../api/Cloudflare';
import { updateComponentsOnCanvasResize } from '../../menu/search/search';
import { CubemapToEquirectangular } from '../../../libs/CubemapToEquirectangular';
import { changeLangToCurrent, getCurrentLangDirection } from '../langSwitcher/langSwitcher';
import { DreamsPreloader } from '../preloader/DreamsPreloader';
import { Compass } from '../compass/compass';
import { Switch } from '../../interfaces/switchers/Switch';
import gsap from 'gsap';

let firstScene = null;
let secondScene = null;
const manager = new LoadingManager();
const textureLoader = new TextureLoader(manager);
let preloader = null;

export class PhotographicViewer {
	constructor (card,data,vantage) {
		if (firstScene === null && secondScene === null) {
			firstScene = new Scene();
			secondScene = new Scene();
		}
		
		this.card = card;
		this.planBox = this.card.querySelector('.options-card__plans-box');
		this.data = data;
		this.vantage = vantage;
		this.element = new BaseElement()
			.addClass('vantage-viewer', isMobile ? 'invisible' : null)
			.appendTo(this.planBox)
			.DOMElement;

		this.fdSwitcherContainer = null;
		this.fdSwitcher = null;
		
		this.viewport = {};
		this.params = {
			matrix: new Matrix4().fromArray(this.vantage.matrix),
			from: new Vector3().fromArray(this.vantage.pointFrom),
			to: new Vector3().fromArray(this.vantage.pointTo),
			parts: ['nz','pz','px','nx','py','ny'],
			url: `${config.cdnRepository}/${config.projectFolder}/`
		};
		
		this.params.from.applyMatrix4(this.params.matrix);
		this.params.to.applyMatrix4(this.params.matrix);
		
		this.camera = threeJsVariables.perspectiveCamera.clone();
		this.camera.fov = this.vantage.fov ? this.vantage.fov : config.features.vantage.fov ? config.features.vantage.fov : 90;
		this.camera.updateProjectionMatrix();
		this.camera.position.copy(this.params.from);
		this.controls = new OrbitControls(this.camera,this.element);
		this.controls.target.copy(this.params.to);
		this.controls.addEventListener('end', () => renderUpdateValuesOnDemand(false));
		this.controls.enablePan = false;
		this.controls.enableZoom = false;
		this.controls.reverseControls = true;
		this.controls.rotateSpeed = 0.15;
		this.controls.update();
		
		const limit = MathUtils.degToRad(this.vantage.limit);
		const currentAzimuth = this.controls.getAzimuthalAngle();
		this.controls.minAzimuthAngle = currentAzimuth - limit / 2;
		this.controls.maxAzimuthAngle = currentAzimuth + limit / 2;
		const polar = config.features.vantage.polarAngle || {max: 1.8, min: 1.2};
		this.controls.minPolarAngle = polar.min;
		this.controls.maxPolarAngle = polar.max;
		
		this.scene = firstScene.children.length ? secondScene : firstScene;
		this.renderer = threeJsVariables.renderer;
		this.canvas = this.renderer.domElement;
		this.calculateViewport();
		this.observer = new ResizeObserver(() => {
			this.calculateViewport();
			setTimeout(() => this.calculateViewport(), 100);
		});
		
		manager.onStart = () => {
			preloader =  new BaseElement()
				.addClass('vantage-preloader')
				.DOMElement;
			this.card.querySelector('.options-card__plans-box').appendChild(preloader);
			new DreamsPreloader(preloader,'vantage-preloader').show();
		};
		
		manager.onLoad = () => {
			if (this.element)
				this.element.classList.remove('invisible');
			renderUpdateValuesOnDemand(false);
			preloader?.remove();
		};
		
		this.observer.observe(this.element);
		this.addSphere();
		this.addFutureDevelopmentSwitcher();
		this.compass = new Compass(this.element,'vantage',this.camera,this.controls);
		this.compass.addClass('vantage-compass');
		this.compass.rotate();
		
		this.controlsOnChange = () => {
			this.compass.rotate();
		};
		
		this.controls.addEventListener('change',this.controlsOnChange);
	}
	
	update(data) {
		renderUpdateValuesOnDemand(false);
		this.vantage = data;
		const vantageURL = isBetaResourcesFolder() ?
			this.vantage.url.replace(/vantages3D/g,'vantages3D_beta') :
			this.vantage.url;
		const url = `${this.params.url}/${vantageURL}/`;
		this.controls.minAzimuthAngle = -Infinity;
		this.controls.maxAzimuthAngle = Infinity;
		this.controls.update();
		let vantageQualityParams = null;
		
		if (config.features.vantage.cloudflare) {
			if (isMobile || isTablet) {
				vantageQualityParams = config.features.vantage.cloudflare.mobile || null;
			} else {
				vantageQualityParams = config.features.vantage.cloudflare.web || null;
			}
		}
		
		this.sphere.traverse(el => {
			if (el.isMesh){
				let modifiedUrl = url + el.name + '.jpg';
				modifiedUrl = Cloudflare.resize(modifiedUrl,vantageQualityParams);
				el.material.uniforms.t.value = 0;
				el.material.uniforms.map1.value = textureLoader.load(modifiedUrl);
			}
		});
		
		this.addFutureDevelopmentSwitcher();
		this.params.matrix = new Matrix4().fromArray(this.vantage.matrix);
		this.params.from = new Vector3().fromArray(this.vantage.pointFrom);
		this.params.to = new Vector3().fromArray(this.vantage.pointTo);
		this.params.from.applyMatrix4(this.params.matrix);
		this.params.to.applyMatrix4(this.params.matrix);
		this.camera.position.copy(this.params.from);
		this.controls.target.copy(this.params.to);
		this.controls.update();
		this.compass.rotate();
		
		this.sphere.position.copy(this.params.from);
		const limit = MathUtils.degToRad(this.vantage.limit);
		
		if (limit === Math.PI * 2) {
			this.controls.minAzimuthAngle = - Infinity;
			this.controls.maxAzimuthAngle = Infinity;
		} else {
			const currentAzimuth = this.controls.getAzimuthalAngle();
			this.controls.minAzimuthAngle = currentAzimuth - limit / 2;
			this.controls.maxAzimuthAngle = currentAzimuth + limit / 2;
		}
		
	}
	
	addFutureDevelopmentSwitcher() {
		if (!this.vantage.futureDevelopment){
			if (this.fdSwitcherContainer) {
				this.fdSwitcherContainer.remove();
			}
			return;
		}
		
		const vantageURL = isBetaResourcesFolder() ?
			this.vantage.url.replace(/vantages3D/g,'vantages3D_beta') :
			this.vantage.url;
		const url = `${this.params.url}/${vantageURL}/`;

		this.sphere.traverse(el => {
			if (el.isMesh){
				let modifiedUrl = url + el.name + '_fd.jpg';
				modifiedUrl = Cloudflare.resize(modifiedUrl);
				el.material.uniforms.map2.value = textureLoader.load(modifiedUrl);
			}
		});
		
		this.fdSwitcherContainer = new BaseElement()
			.addClass('vantage__future-development','background-backdrop-filter')
			.appendTo(this.planBox)
			.DOMElement;
		
		this.fdSwitcher = new BaseElement('button')
			.addClass('icon-construction-site','vantage__future-development-btn')
			.event('click',this.futureDevelopmentHandler.bind(this))
			.appendTo(this.fdSwitcherContainer)
			.DOMElement;
		
		new BaseElement('span')
			.addClass('vantage__future-development__title','language-string','background-backdrop-filter')
			.addAttribute('data-dictionary','Future development')
			.innerHtml(changeLangToCurrent('Future development'))
			.appendTo(this.fdSwitcherContainer);
		
	}
	
	futureDevelopmentHandler(e) {
		this.fdSwitcher.classList.toggle('show_future-development');
		
		const materials = [];
		this.sphere.traverse(el => {
			if (el.isMesh){
				materials.push(el.material);
			}
		});
		
		const state = this.fdSwitcher.classList.contains('show_future-development');
		const from = {value: state ? 0 : 1};
		const to = state ? 1 : 0;
		
		gsap.to(from,{
			value: to,
			duration: GSAPAnimationMixer.duration,
			ease: GSAPAnimationMixer.ease,
			onUpdate: () => {
				materials.forEach(material => material.uniforms.t.value = from.value);
			}
		});
	}
	
	addSphere() {
		this.sphere = new Group();
		this.sphere.name = 'Vantage';
		const vantageURL = isBetaResourcesFolder() ?
			this.vantage.url.replace(/vantages3D/g,'vantages3D_beta') :
			this.vantage.url;
		const url = `${this.params.url}/${vantageURL}/`;
		
		let vantageQualityParams = null;
		
		if (config.features.vantage.cloudflare) {
			if (isMobile || isTablet) {
				vantageQualityParams = config.features.vantage.cloudflare.mobile || null;
			} else {
				vantageQualityParams = config.features.vantage.cloudflare.web || null;
			}
		}
		
		this.params.parts.forEach(name => {
			let mesh;
			let modifiedUrl;
			
			switch (name) {
				case 'nz':
					mesh = generateRegion([0,0,-1],[0,1,0],[ 1,0,0]);
					break;
				case 'pz':
					mesh = generateRegion([0,0, 1],[0,1,0],[-1,0,0]);
					break;
				case 'px':
					mesh = generateRegion([ 1,0,0],[0,1,0],[0,0, 1]);
					break;
				case 'nx':
					mesh = generateRegion([-1,0,0],[0,1,0],[0,0,-1]);
					break;
				case 'py':
					mesh = generateRegion([0,0, 1],[1,0,0],[0, 1,0]);
					break;
				case 'ny':
					mesh = generateRegion([0,0,-1],[1,0,0],[0,-1,0]);
					mesh.rotation.y = Math.PI;
					break;
			}
			
			modifiedUrl = url + name + '.jpg';
			modifiedUrl = Cloudflare.resize(modifiedUrl,vantageQualityParams);
			mesh.material.uniforms.map1.value = textureLoader.load(modifiedUrl);
			mesh.name = name;
			this.sphere.position.copy(this.params.from);
			this.sphere.add(mesh);
		});
		this.scene.add(this.sphere);
	}
	
	calculateViewport() {
		if (!this.element)
			return;

		const rect = this.element.getBoundingClientRect();
		if (isMobile) {
			if (isPortraitMode()) {
				this.viewport.width = rect.right - rect.left;
				this.viewport.height = rect.bottom - rect.top;
				this.viewport.left = rect.left;
				this.viewport.bottom = this.renderer.domElement.clientHeight - rect.height;
			} else {
				this.viewport.width = rect.right - rect.left;
				this.viewport.height = rect.bottom - rect.top;
				this.viewport.left = 0;
				this.viewport.bottom = 0;
			}
		} else {
			// const offset = getCurrentLangDirection() === 'ltr' ? 56 : 0;
			const offset = 0;
			this.viewport.width = rect.right - rect.left;
			this.viewport.height = rect.bottom - rect.top;
			this.viewport.left = rect.left - offset;
			this.viewport.bottom = this.renderer.domElement.clientHeight - rect.bottom;
		}
		
		this.resize();
		this.draw();
	}
	
	resize() {
		this.camera.aspect = this.viewport.width / this.viewport.height;
		this.camera.updateProjectionMatrix();
	}
	
	draw() {
		this.canvas.style.transform = `translateY(${-this.card.scrollTop}px)`;
		this.canvas.style.transition = 'transform .1s';
		this.renderer.setViewport( this.viewport.left, this.viewport.bottom, this.viewport.width, this.viewport.height );
		this.renderer.setScissor( this.viewport.left, this.viewport.bottom, this.viewport.width, this.viewport.height );
		this.renderer.render(this.scene,this.camera);
	}
	
	dispose() {
		this.sphere.removeFromParent();
		this.sphere.traverse(el => {
			if (el.isMesh) {
				el.geometry.dispose();
				el.material.uniforms.map1.value.dispose();
				el.material.dispose();
			}
		});
		
		this.canvas.style.transform = '';
		this.canvas.style.transition = '';
		this.controls.removeEventListener('change',this.controlsOnChange);
		this.controlsOnChange = null;
		this.controls.dispose();
		this.element.remove();
		this.observer.disconnect();
		this.compass.dispose();
		this.compass = null;
		this.element = null;
		this.observer = null;
		this.controls = null;
		this.sphere = null;
		this.scene = null;
		this.camera = null;
		updateComponentsOnCanvasResize();
	}
	
	getEquirectangular(size,rotation = -90, flip = false) {
		const cubeRenderTarget = new WebGLCubeRenderTarget(size);
		const cubeCamera = new CubeCamera(0.1,8000,cubeRenderTarget);
		
		cubeCamera.position.copy(this.camera.position);
		cubeCamera.rotation.y = MathUtils.degToRad(rotation);
		cubeCamera.updateMatrixWorld(true);
		cubeCamera.update(this.renderer,this.scene);
		
		const equirectangular = new CubemapToEquirectangular(this.renderer,size,false,flip);
		equirectangular.convert(cubeCamera);
	}
	
}
